// /src/utils/langCookie.js

export const LANG_COOKIE = "app.lang";
export const ENABLED_LANGS = ["ZH", "EN", "JP"];
export const DEFAULT_LANG = "ZH";

function normalizeLang(code) {
  const value = String(code || "").trim().toUpperCase();
  return ENABLED_LANGS.includes(value) ? value : DEFAULT_LANG;
}

function getSharedCookieDomain() {
  if (typeof window === "undefined") {
    return "";
  }

  const hostname = String(window.location.hostname || "").toLowerCase();

  /*
   * Share the language cookie between:
   * - sehopro.com
   * - www.sehopro.com
   * - any future subdomain under sehopro.com
   */
  if (hostname === "sehopro.com" || hostname.endsWith(".sehopro.com")) {
    return ".sehopro.com";
  }

  /*
   * Localhost / staging / IP address should stay host-only.
   */
  return "";
}

function getSecureCookiePart() {
  const isHttps =
    typeof window !== "undefined" && window.location?.protocol === "https:";

  return isHttps ? "; Secure" : "";
}

function getCookieExpiry(days) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  return `expires=${date.toUTCString()}`;
}

function readCookieValue(name) {
  if (typeof document === "undefined") {
    return "";
  }

  const cookieName = `${name}=`;
  const parts = document.cookie.split(";").map((item) => item.trim());

  for (const part of parts) {
    if (part.startsWith(cookieName)) {
      try {
        return decodeURIComponent(part.slice(cookieName.length));
      } catch {
        return part.slice(cookieName.length);
      }
    }
  }

  return "";
}

function writeCookie(code, days = 365, domain = "") {
  if (typeof document === "undefined") {
    return;
  }

  const safeCode = normalizeLang(code);
  const expires = getCookieExpiry(days);
  const secure = getSecureCookiePart();
  const domainPart = domain ? `; Domain=${domain}` : "";

  document.cookie = `${LANG_COOKIE}=${encodeURIComponent(
    safeCode,
  )}; ${expires}; path=/${domainPart}; SameSite=Lax${secure}`;
}

function expireCookie(domain = "") {
  if (typeof document === "undefined") {
    return;
  }

  const secure = getSecureCookiePart();
  const domainPart = domain ? `; Domain=${domain}` : "";

  document.cookie = `${LANG_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainPart}; SameSite=Lax${secure}`;
}

/*
 * Main language reader.
 *
 * The cookie is now the source of truth.
 *
 * On production SehoPro domains, the cookie is always written as:
 * Domain=.sehopro.com
 *
 * That makes the same app.lang value available on:
 * - sehopro.com
 * - www.sehopro.com
 */
export function getLangFromCookie() {
  if (typeof document === "undefined") {
    return DEFAULT_LANG;
  }

  const sharedDomain = getSharedCookieDomain();
  const currentCookieValue = readCookieValue(LANG_COOKIE);
  const safeCode = normalizeLang(currentCookieValue || DEFAULT_LANG);

  if (sharedDomain) {
    /*
     * Rewrite the current value as the shared .sehopro.com cookie.
     */
    writeCookie(safeCode, 365, sharedDomain);

    /*
     * Remove old host-only cookie on the current host.
     *
     * This prevents cases like:
     * sehopro.com app.lang = ZH
     * www.sehopro.com app.lang = EN
     */
    expireCookie();
  } else if (!currentCookieValue) {
    writeCookie(safeCode, 365);
  }

  return safeCode;
}

/*
 * Main language setter.
 *
 * This no longer depends on localStorage.
 */
export function setLangCookie(code, days = 365) {
  if (typeof document === "undefined") {
    return;
  }

  const safeCode = normalizeLang(code);
  const sharedDomain = getSharedCookieDomain();

  /*
   * Remove old host-only cookie on the current host.
   */
  expireCookie();

  if (sharedDomain) {
    /*
     * Replace the shared cookie.
     */
    expireCookie(sharedDomain);
    writeCookie(safeCode, days, sharedDomain);
  } else {
    writeCookie(safeCode, days);
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("app:languageChanged"));
  }
}